/**
 * Auth resource hooks. Single endpoint: `GET /api/auth/me`.
 *
 * Wraps the Orval-generated `authMe` fetcher to add three pieces of
 * behaviour the raw generated hook doesn't have:
 *  1. `AUTH_ME_QUERY_KEY` — a fixed key shared with `usePrincipal`
 *     so the principal cache stays single-shape across consumers.
 *  2. `fetchAuthMeOrAnonymous` — historical wrapper retained for call-site
 *     compatibility. Under the post-MSAL contract it delegates straight
 *     through to `fetchAuthMe`: 401/403 is no longer swallowed (see
 *     docblock on the function itself). The "Or Anonymous" name lies —
 *     it never returns null — and the export is preserved only to save a
 *     rename ripple across `usePrincipal` and its tests.
 *  3. `useAuthMe` — typed to `GetAuthMeResponse` since the previous
 *     anonymous-fallback contract is gone. 401 surfaces as `isError` and
 *     `usePrincipal` translates that into `loginRedirect`.
 *
 * Consumption contract (kept in sync with `hooks/usePrincipal.ts`):
 * the React Query cache entry resolves to `GetAuthMeResponse` on success
 * and an `ApiError` on failure — there is no `null` settled state. Boot-
 * time anonymous access is handled by `AuthedLayout` redirecting before
 * the query ever runs; mid-session 401 surfaces via `query.error` and
 * the principal hook fires `loginRedirect` off the `ApiError.status`.
 *
 * Types are pulled from `@cotality/marketplace-contracts/endpoints/auth`
 * so the principal shape is locked to the shared contract — drift surfaces
 * as a TS error.
 */
import { authMe } from '@/api/generated';
import { ApiError } from '@/api/mutator';
import type { GetAuthMeResponse } from '@cotality/marketplace-contracts/endpoints/auth';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';

/**
 * Re-export so consumers needing to branch on the auth error (notably
 * `usePrincipal`'s mid-session 401 → loginRedirect path) can pull
 * the type from a hooks-layer module — the `lint-contracts.sh` Guard 3
 * forbids direct `@/api/mutator` imports outside `src/api/`. Conceptually
 * the auth hook is the canonical consumer of auth-error classification,
 * so re-exporting from here is symmetric with where the call lives.
 */
export { ApiError };

export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * Raw fetch — throws `ApiError` on every non-2xx, including 401/403. The
 * mutator surfaces the status, so `usePrincipal` can branch on a
 * 401 to trigger an MSAL `loginRedirect` (mid-session token rejection
 * recovery path).
 */
export function fetchAuthMe(signal?: AbortSignal): Promise<GetAuthMeResponse> {
  // The generated `authMe` returns `AuthMe200` (the envelope shape) at
  // the TS layer; the mutator unwraps the envelope at runtime so the
  // resolved value is the inner principal payload — same as
  // `GetAuthMeResponse` in the shared contract.
  return authMe(signal) as Promise<GetAuthMeResponse>;
}

/**
 * Post-MSAL contract: 401/403 from `/api/auth/me` means the MSAL-cached
 * access token is no longer accepted — re-auth is required. The hook
 * surfaces the error so `usePrincipal` can trigger `loginRedirect`.
 *
 * The "Or Anonymous" suffix is retained for call-site compatibility; the
 * legacy 401-swallow behaviour is intentionally gone (was load-bearing
 * under SWA Easy Auth's anonymous-catalog flow, which doesn't apply now
 * that the API is MSAL-bearer-gated). Every code path either resolves to
 * the principal or throws `ApiError` — the historical `| null` in the
 * return type was a lie and has been dropped.
 */
export async function fetchAuthMeOrAnonymous(signal?: AbortSignal): Promise<GetAuthMeResponse> {
  return fetchAuthMe(signal);
}

/**
 * React Query hook for `GET /api/auth/me`. Resolves to the principal on
 * success; 401/403 surface as `isError` (the legacy anonymous-fallback
 * contract is gone — see `fetchAuthMeOrAnonymous`). Most callers that
 * need permission helpers should consume `usePrincipal` instead — it
 * composes the same cache entry, exposes a convenience surface, and
 * additionally translates a 401 into an MSAL `loginRedirect`.
 */
export function useAuthMe(): UseQueryResult<GetAuthMeResponse> {
  return useQuery<GetAuthMeResponse>({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: ({ signal }) => fetchAuthMeOrAnonymous(signal),
    staleTime: 5 * 60_000,
    retry: false,
    // Post-MSAL contract: a 401 from /api/auth/me settles the query into an
    // error state and `usePrincipal` reacts by firing `loginRedirect`.
    // React Query's default `retryOnMount: true` would re-fetch the moment
    // a SECOND observer mounts against the same errored cache entry (e.g.
    // RootGate already subscribed, then LandingRoute mounts) — and because
    // the SPA fans out the principal to many consumers (RootGate +
    // LandingRoute, AuthedLayout + RequirePermissions, sidebar UserMenu,
    // …), each new observer would refire the request. The result is an
    // observable refetch storm and a UI stuck on `LoadingScreen` because
    // `isLoading` flips back true each pass. `retryOnMount: false` makes
    // the error authoritative; loginRedirect drives the recovery path.
    retryOnMount: false,
  });
}
